import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError } from '../api';
import { money, date, useTenantCatalog } from '../hooks';
import { Modal } from '../components/Modal';
import { Icon } from '../shared/Icon';
import { InvoiceStatus } from './DashboardPage';
import type { Invoice, Page } from '../types';

/** Счета тенанта и запуск расчёта за период. */
export function InvoicesPage() {
  const { tenantId = '' } = useParams();
  const queryClient = useQueryClient();
  const catalog = useTenantCatalog(tenantId);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Invoice[] | null>(null);
  const [period, setPeriod] = useState(defaultPeriod());

  const invoices = useQuery({
    queryKey: ['invoices', tenantId],
    queryFn: () => api.get<Page<Invoice>>(`/tenants/${tenantId}/invoices?size=100&sort=issuedAt,desc`),
  });

  const run = useMutation({
    mutationFn: () =>
      api.post<Invoice[]>(`/tenants/${tenantId}/invoice-runs`, {
        periodStart: period.start,
        periodEnd: period.end,
      }),
    onSuccess: (issued) => {
      setResult(issued);
      queryClient.invalidateQueries({ queryKey: ['invoices', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['ledger', tenantId] });
    },
    onError: (err) => setError((err as ApiError).message),
  });

  const rows = invoices.data?.content ?? [];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Счета</h1>
          <div className="muted small">{invoices.data ? `Всего: ${invoices.data.totalElements}` : ' '}</div>
        </div>
        <div className="spacer" />
        <button
          className="btn btn-primary"
          onClick={() => { setError(null); setResult(null); setRunning(true); }}
        >
          <Icon name="plus" />
          Выставить за период
        </button>
      </div>

      {invoices.isLoading ? (
        <div className="state">Загрузка…</div>
      ) : rows.length === 0 ? (
        <div className="card">
          <div className="state-title">Счетов пока нет</div>
          <p className="muted">
            Запустите расчёт за период — система выставит счёт по каждому действующему договору.
          </p>
          <div className="state-actions">
            <button className="btn btn-primary" onClick={() => setRunning(true)}>
              Выставить за период
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Счёт</th>
                <th>Абонент</th>
                <th>Период</th>
                <th className="num">Сумма</th>
                <th className="num">Оплачено</th>
                <th>Оплатить до</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <Link to={`/t/${tenantId}/invoices/${invoice.id}`}>{invoice.number}</Link>
                  </td>
                  <td>{catalog.customerById.get(invoice.customerId)?.name ?? '—'}</td>
                  <td className="muted small">
                    {date(invoice.periodStart)} — {date(invoice.periodEnd)}
                  </td>
                  <td className="num">{money(invoice.totalKopecks)}</td>
                  <td className="num">{money(invoice.paidKopecks)}</td>
                  <td className="muted small">{date(invoice.dueDate)}</td>
                  <td>
                    <InvoiceStatus status={invoice.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {running && (
        <Modal title="Расчёт за период" onClose={() => setRunning(false)}>
          <div className="row">
            <div className="form-group">
              <label className="form-label" htmlFor="run-from">
                с
              </label>
              <input
                id="run-from"
                className="input"
                type="date"
                value={period.start}
                onChange={(e) => setPeriod({ ...period, start: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="run-to">
                по
              </label>
              <input
                id="run-to"
                className="input"
                type="date"
                value={period.end}
                onChange={(e) => setPeriod({ ...period, end: e.target.value })}
              />
              <div className="form-hint">Конец периода не включается.</div>
            </div>
          </div>

          <div className="form-hint">
            Повторный запуск безопасен: договоры, по которым счёт за этот период уже выставлен,
            пропускаются.
          </div>

          {error && <div className="form-error">{error}</div>}
          {result && (
            <div className={result.length ? 'success-box' : 'state'}>
              {result.length
                ? `Выставлено счетов: ${result.length} на ${money(result.reduce((s, i) => s + i.totalKopecks, 0))} ₽`
                : 'Ничего не выставлено — за этот период всё уже посчитано либо начислять нечего.'}
            </div>
          )}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setRunning(false)}>
              Закрыть
            </button>
            <button
              className="btn btn-primary"
              disabled={run.isPending}
              onClick={() => { setError(null); run.mutate(); }}
            >
              Выставить
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

function defaultPeriod() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end) };
}
